import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Spin, message, Table, Tabs, Button, Descriptions } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { getInternalCustomerDetails } from "../../../core/api/database";
import moment from "moment";

const { TabPane } = Tabs;

const InternalCustomerDetail = ({ customerId }) => {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Use either the prop or the URL parameter
  const effectiveId = customerId || paramId;

  useEffect(() => {
    if (effectiveId) {
      fetchCustomerDetails();
    }
  }, [effectiveId]);

  const fetchCustomerDetails = async () => {
    setLoading(true);
    try {
      const response = await getInternalCustomerDetails(effectiveId);
      setCustomerData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching customer details:", error);
      message.error("Failed to fetch customer details");
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Format currency values
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spin size="large" />
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="text-center p-5">
        <h3>No customer data found</h3>
        <Button type="primary" onClick={handleBack}>
          <ArrowLeftOutlined /> Back
        </Button>
      </div>
    );
  }

  const { customer_info, campaign_data, order_count, order_stats } = customerData;

  // Prepare data for recent orders table
  const recentOrdersColumns = [
    {
      title: 'Order Number',
      dataIndex: 'order_num',
      key: 'order_num',
    },
    {
      title: 'Purchase Date',
      dataIndex: 'purch_date',
      key: 'purch_date',
      render: (date) => date ? moment(date).format('MM/DD/YYYY') : 'N/A',
    },
    {
      title: 'Amount',
      dataIndex: 'order_sale_amt',
      key: 'order_sale_amt',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Product',
      dataIndex: 'camp_prod_desc',
      key: 'camp_prod_desc',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
    },
    {
      title: 'List',
      dataIndex: 'list',
      key: 'list',
    },
  ];

  return (
    <div className="customer-detail-page">
      <div className="mb-4">
        <Button type="primary" onClick={handleBack}>
          <ArrowLeftOutlined /> Back to Customer List
        </Button>
      </div>

      <Card title={`Customer: ${customer_info.name || 'Unknown'}`} className="mb-4">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Customer Data" className="h-100">
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Name">{customer_info.name || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Job Title">{customer_info.jobtitle || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Company">{customer_info.company || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Email">{customer_info.email || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{customer_info.phone || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Address">
                  {customer_info.address1 ? (
                    <>
                      {customer_info.address1}<br />
                      {customer_info.address2 && <>{customer_info.address2}<br /></>}
                      {customer_info.address3 && <>{customer_info.address3}<br /></>}
                      {customer_info.city}, {customer_info.province} {customer_info.postcode}<br />
                      {customer_info.countrycode}
                    </>
                  ) : (
                    'N/A'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Sex">{customer_info.sex_code || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Suppress Reason">{customer_info.suppress_reason || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Notes">{customer_info.notes || 'N/A'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          
          <Col xs={24} md={12}>
            <Card title="Customer Statistics" className="h-100">
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Net P&L">{formatCurrency(customer_info.net_pnl || 0)}</Descriptions.Item>
                <Descriptions.Item label="Total Sales">{formatCurrency(customer_info.total_sales || 0)}</Descriptions.Item>
                <Descriptions.Item label="Number of Mailings">{customer_info.number_of_mailings || 0}</Descriptions.Item>
                <Descriptions.Item label="Total Orders">{order_count}</Descriptions.Item>
                {order_stats && order_stats.total_sales && (
                  <>
                    <Descriptions.Item label="Order Sales">{formatCurrency(order_stats.total_sales)}</Descriptions.Item>
                    <Descriptions.Item label="Order Product Cost">{formatCurrency(order_stats.total_product_cost)}</Descriptions.Item>
                    <Descriptions.Item label="Order Net P&L">{formatCurrency(order_stats.net_pnl)}</Descriptions.Item>
                    {order_stats.avg_order_value && (
                      <Descriptions.Item label="Average Order Value">{formatCurrency(order_stats.avg_order_value)}</Descriptions.Item>
                    )}
                  </>
                )}
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </Card>

      <Tabs defaultActiveKey="1">
        <TabPane tab="Recent Orders" key="1">
          <Card>
            {order_stats && order_stats.recent_orders && order_stats.recent_orders.length > 0 ? (
              <Table 
                dataSource={order_stats.recent_orders} 
                columns={recentOrdersColumns} 
                rowKey={(record) => record.order_num}
                pagination={false}
              />
            ) : (
              <div className="text-center p-3">No recent orders found</div>
            )}
          </Card>
        </TabPane>
        
        <TabPane tab="Campaign Information" key="2">
          <Row gutter={[16, 16]}>
            {campaign_data && Object.keys(campaign_data).length > 0 ? (
              <>
                <Col xs={24} md={8}>
                  <Card title="Campaign Codes">
                    {campaign_data.campaign_codes && Object.keys(campaign_data.campaign_codes).length > 0 ? (
                      <ul>
                        {Object.entries(campaign_data.campaign_codes).map(([code, count]) => (
                          <li key={code}>
                            <strong>{code}:</strong> {count} records
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div>No campaign codes found</div>
                    )}
                  </Card>
                </Col>
                
                <Col xs={24} md={8}>
                  <Card title="Client Codes">
                    {campaign_data.client_codes && Object.keys(campaign_data.client_codes).length > 0 ? (
                      <ul>
                        {Object.entries(campaign_data.client_codes).map(([code, count]) => (
                          <li key={code}>
                            <strong>{code}:</strong> {count} records
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div>No client codes found</div>
                    )}
                  </Card>
                </Col>
                
                <Col xs={24} md={8}>
                  <Card title="Offer Codes">
                    {campaign_data.offer_codes && Object.keys(campaign_data.offer_codes).length > 0 ? (
                      <ul>
                        {Object.entries(campaign_data.offer_codes).map(([code, count]) => (
                          <li key={code}>
                            <strong>{code}:</strong> {count} records
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div>No offer codes found</div>
                    )}
                  </Card>
                </Col>
              </>
            ) : (
              <Col span={24}>
                <div className="text-center p-3">No campaign information found</div>
              </Col>
            )}
          </Row>
        </TabPane>
        
        <TabPane tab="Order Statistics" key="3">
          <Row gutter={[16, 16]}>
            {order_stats && Object.keys(order_stats).length > 0 ? (
              <>
                <Col xs={24} md={12}>
                  <Card title="Orders by Source">
                    {order_stats.sources && Object.keys(order_stats.sources).length > 0 ? (
                      <ul>
                        {Object.entries(order_stats.sources).map(([source, count]) => (
                          <li key={source}>
                            <strong>{source}:</strong> {count} orders
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div>No source information found</div>
                    )}
                  </Card>
                </Col>
                
                <Col xs={24} md={12}>
                  <Card title="Orders by List">
                    {order_stats.lists && Object.keys(order_stats.lists).length > 0 ? (
                      <ul>
                        {Object.entries(order_stats.lists).map(([list, count]) => (
                          <li key={list}>
                            <strong>{list}:</strong> {count} orders
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div>No list information found</div>
                    )}
                  </Card>
                </Col>
              </>
            ) : (
              <Col span={24}>
                <div className="text-center p-3">No order statistics found</div>
              </Col>
            )}
          </Row>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default InternalCustomerDetail;
